/**
 * Sensitivity-label coverage Graph calls (C12). For a site's default document
 * library, count how many files carry a sensitivity label. Behind the graph/ layer,
 * least-privilege, zero-egress.
 *
 * Label detection is best-effort under delegated Graph: the driveItem
 * `sensitivityLabel` facet (beta) is read where the tenant exposes it; where it is
 * not surfaced, files are counted as unlabelled and the UI says so — consistent
 * with the app's honesty posture (never imply more assurance than exists).
 */
import { graphGetPages } from '@/graph/client';
import { CAPABILITY_SCOPES } from '@/auth/scopes';
import type { SiteRef } from '@/graph/modules/sites';

const SCOPES = CAPABILITY_SCOPES.C12.scopes;

export const LABEL_ENDPOINTS = {
  driveChildren: '/sites/{siteId}/drive/root/children',
} as const;

export interface SiteLabelCoverage {
  siteId: string;
  displayName: string;
  webUrl: string;
  scanned: number;
  labeled: number;
  /** label displayName -> file count */
  byLabel: Record<string, number>;
}

interface DriveItemLite {
  id?: string;
  name?: string;
  file?: unknown;
  folder?: unknown;
  sensitivityLabel?: { displayName?: string | null; id?: string | null } | null;
}

/** Scan one site's document library (bounded) and tally label coverage. */
export async function getSiteLabelCoverage(
  site: SiteRef,
  maxFiles: number,
): Promise<SiteLabelCoverage> {
  const cov: SiteLabelCoverage = {
    siteId: site.id,
    displayName: site.displayName,
    webUrl: site.webUrl,
    scanned: 0,
    labeled: 0,
    byLabel: {},
  };

  const path = `/sites/${encodeURIComponent(site.id)}/drive/root/children`;
  try {
    for await (const page of graphGetPages<DriveItemLite>(path, {
      scopes: SCOPES,
      version: 'beta',
      query: { $select: 'id,name,file,folder,sensitivityLabel', $top: '200' },
    })) {
      for (const item of page) {
        if (item.folder || !item.file) continue; // count files only
        cov.scanned += 1;
        const label = item.sensitivityLabel?.displayName;
        if (label) {
          cov.labeled += 1;
          cov.byLabel[label] = (cov.byLabel[label] ?? 0) + 1;
        }
        if (cov.scanned >= maxFiles) return cov;
      }
    }
  } catch {
    // Site/library not readable, or label facet unavailable — return what we have.
  }
  return cov;
}
